/**
 * Global property enrichment.
 *
 * Attaches standard context to every event: environment, app version,
 * page path, UTM params, session ID, and active feature flags.
 *
 * Also owns the shared `reklaim_attribution` localStorage bridge helpers
 * (UTM + Meta click/campaign IDs). `getUTMParams` / `utm_params` global
 * enrichment is unchanged and independent of the attribution blob.
 */

import { getAnalyticsConfig } from './config';

const ATTRIBUTION_KEY = 'reklaim_attribution';

let _sessionId: string | null = null;

function getSessionId(): string {
  if (!_sessionId) {
    _sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }
  return _sessionId;
}

function getUTMParams(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const utm: Record<string, string> = {};
  for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']) {
    const val = params.get(key);
    if (val) utm[key] = val;
  }
  if (Object.keys(utm).length > 0) {
    try { localStorage.setItem('utm_params', JSON.stringify(utm)); } catch { /* localStorage unavailable */ }
  } else {
    try {
      const stored = localStorage.getItem('utm_params');
      if (stored) Object.assign(utm, JSON.parse(stored));
    } catch { /* localStorage unavailable or corrupt */ }
  }
  return utm;
}

export interface GlobalProperties {
  event_id: string;
  timestamp: string;
  session_id: string;
  platform: 'web';
  environment: string;
  app_version: string;
  page_path: string;
  referrer: string;
  [key: string]: string | number | boolean | undefined;
}

export function getGlobalProperties(): GlobalProperties {
  const config = getAnalyticsConfig();
  const utm = getUTMParams();

  return {
    event_id: globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).substring(2)}`,
    timestamp: new Date().toISOString(),
    session_id: getSessionId(),
    platform: 'web',
    environment: config.environment,
    app_version: config.appVersion,
    page_path: typeof window !== 'undefined' ? window.location.pathname : '',
    referrer: typeof document !== 'undefined' ? document.referrer : '',
    ...utm,
  };
}

/** Shape stored in `localStorage.reklaim_attribution` (UTM + Meta click IDs). */
export interface ReklaimAttribution {
  ref?: string;
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  term?: string;
  fbclid?: string;
  campaign_id?: string;
  adset_id?: string;
  ad_id?: string;
  landedAt?: string;
}

const ATTRIBUTION_STRING_KEYS = [
  'ref',
  'source',
  'medium',
  'campaign',
  'content',
  'term',
  'fbclid',
  'campaign_id',
  'adset_id',
  'ad_id',
  'landedAt',
] as const satisfies ReadonlyArray<keyof ReklaimAttribution>;

/** Copy only allowlisted string attribution keys (rejects prototype pollution). */
function pickAttributionFields(raw: Record<string, unknown>): ReklaimAttribution {
  const out: ReklaimAttribution = {};
  for (const key of ATTRIBUTION_STRING_KEYS) {
    const value = raw[key];
    if (typeof value === 'string' && value.length > 0) {
      out[key] = value;
    }
  }
  return out;
}

/**
 * Read UTM / ref / Meta click params from a URLSearchParams (or current location).
 * Does not touch storage. Empty object when nothing attribution-related is present.
 */
export function readAttributionParams(
  searchParams?: URLSearchParams | null,
): Partial<ReklaimAttribution> {
  if (typeof window === 'undefined' && !searchParams) return {};
  const params =
    searchParams ??
    (typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null);
  if (!params) return {};

  const out: Partial<ReklaimAttribution> = {};
  const ref = params.get('ref');
  const source = params.get('utm_source');
  const medium = params.get('utm_medium');
  const campaign = params.get('utm_campaign');
  const content = params.get('utm_content');
  const term = params.get('utm_term');
  const fbclid = params.get('fbclid');
  const campaignId = params.get('campaign_id');
  const adsetId = params.get('adset_id');
  const adId = params.get('ad_id');

  if (ref) out.ref = ref;
  if (source) out.source = source;
  if (medium) out.medium = medium;
  if (campaign) out.campaign = campaign;
  if (content) out.content = content;
  if (term) out.term = term;
  if (fbclid) out.fbclid = fbclid;
  if (campaignId) out.campaign_id = campaignId;
  if (adsetId) out.adset_id = adsetId;
  if (adId) out.ad_id = adId;
  return out;
}

export function hasAttributionFields(patch: Partial<ReklaimAttribution>): boolean {
  return Boolean(
    patch.ref ||
      patch.source ||
      patch.medium ||
      patch.campaign ||
      patch.content ||
      patch.term ||
      patch.fbclid ||
      patch.campaign_id ||
      patch.adset_id ||
      patch.ad_id,
  );
}

/** Read existing `reklaim_attribution` blob (or `{}` if missing/corrupt). */
export function readStoredAttribution(): ReklaimAttribution {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(ATTRIBUTION_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return pickAttributionFields(parsed as Record<string, unknown>);
    }
  } catch {
    // corrupt — start fresh
  }
  return {};
}

/**
 * Merge attribution fields into `reklaim_attribution`, preserving prior keys
 * and stamping `landedAt` on first write. No-op (returns current) when patch
 * has no attribution fields.
 */
export function mergeAttributionIntoStorage(
  patch: Partial<ReklaimAttribution>,
): ReklaimAttribution {
  if (typeof window === 'undefined' || !hasAttributionFields(patch)) {
    return readStoredAttribution();
  }

  const attribution: ReklaimAttribution = { ...readStoredAttribution() };
  if (patch.ref) attribution.ref = patch.ref;
  if (patch.source) attribution.source = patch.source;
  if (patch.medium) attribution.medium = patch.medium;
  if (patch.campaign) attribution.campaign = patch.campaign;
  if (patch.content) attribution.content = patch.content;
  if (patch.term) attribution.term = patch.term;
  if (patch.fbclid) attribution.fbclid = patch.fbclid;
  if (patch.campaign_id) attribution.campaign_id = patch.campaign_id;
  if (patch.adset_id) attribution.adset_id = patch.adset_id;
  if (patch.ad_id) attribution.ad_id = patch.ad_id;
  attribution.landedAt = attribution.landedAt || new Date().toISOString();

  try {
    localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
  } catch {
    /* localStorage unavailable */
  }
  return attribution;
}

/**
 * Capture URL attribution (UTM + Meta + ref) into `reklaim_attribution`.
 * Keeps `getUTMParams` / `utm_params` enrichment independent.
 */
export function captureAttributionFromSearchParams(
  searchParams?: URLSearchParams | null,
): ReklaimAttribution {
  return mergeAttributionIntoStorage(readAttributionParams(searchParams));
}

/**
 * Filterable entry source for funnel events: prefer utm/blob `source`, else `ref`, else `"direct"`.
 */
export function resolveEntrySource(attribution?: Partial<ReklaimAttribution> | null): string {
  const blob = attribution ?? readStoredAttribution();
  if (blob.source) return blob.source;
  if (blob.ref) return blob.ref;
  return 'direct';
}
